import { useFormContext } from 'react-hook-form';
import { RadioField, SelectField, NumberSelectField, TextField } from '@views/register/history/history-fields';
import type { TLocalize } from '@hooks/use-localize';
import type { IHistoryDiagnosedFormValues, IHistoryDiagnosedModel } from '@/types/history/history-diagnosed';
import FieldError from '@components/field-error';

interface IProps {
    model: IHistoryDiagnosedModel;
    t: TLocalize;
}

const CardiovascularSection = ({ model, t }: IProps) => {
    const {
        watch,
        register,
        formState: { errors },
    } = useFormContext<IHistoryDiagnosedFormValues>();

    const ms = model.medicalHistoryStatusOptions;
    const yn = model.yesNoUnknownOptions;
    const yes = String(model.yesValue);
    const still = String(model.medicalStatusStillBeingTreatedValue);
    const please = t('DashLabel_PleaseSelect', 'Please Select');

    return (
        <>
            <div className='section-heading'>Cardiovascular</div>

            <RadioField
                name="hasHistoryOfHighBp"
                label={t('DashLabel_DoYouHaveAHistoryOfHighBloodPressure', 'Do you have a history of high blood pressure')}
                options={ms}
            />
            {watch('hasHistoryOfHighBp') === still && (
                <div className="form-group sub-question">
                    <label className="control-label col-xs-12">
                        {t('DashLabel_HowManyBloodPressureAgentsMedicationsAreYouOn', 'How many blood pressure agents (medications) are you on')}?
                        <FieldError message={errors.highBpAgents?.message} />
                    </label>
                    <div className="col-xs-12">
                        <label className="radio-inline">
                            <input type="radio" value="0" {...register('highBpAgents')} /> {t('Word_None', 'None')}
                        </label>
                        <label className="radio-inline">
                            <input type="radio" value="1" {...register('highBpAgents')} /> 1
                        </label>
                        <label className="radio-inline">
                            <input type="radio" value="2" {...register('highBpAgents')} /> 2
                        </label>
                        <label className="radio-inline">
                            <input type="radio" value="3" {...register('highBpAgents')} /> 3 {t('DashLabel_OrMore', 'or more')}
                        </label>
                    </div>
                </div>
            )}

            {model.requirePregnancyFields && (
                <RadioField
                    name="hadHighBpDuringPregnancy"
                    label={t('DashLabel_WhenPregnantWereYouTreatedForHighBloodPressure', 'When pregnant, were you treated for high blood pressure')}
                    options={ms}
                />
            )}

            <RadioField
                name="hasHadCabg"
                label={t('DashLabel_HaveYouEverHadCoronaryArteryBypassGraftingBypassSu', 'Have you ever had Coronary Artery Bypass Grafting (bypass surgery)')}
                options={yn}
            />

            <RadioField
                name="hasHistoryOfBloodClots"
                label={t('DashLabel_DoYouHaveAHistoryOfBloodClots', 'Do you have a history of blood clots')}
                options={ms}
            />

            {!model.useReducedQuestions && (
                <RadioField
                    name="hasHistoryOfCoronaryDisease"
                    label={t('DashLabel_DoYouHaveAHistoryOfCoronaryArteryDiseaseHeartDisea', 'Do you have a history of coronary artery disease (heart disease)')}
                    options={ms}
                />
            )}

            <RadioField
                name="hasHistoryOfHighCholOrTg"
                label={t('DashLabel_DoYouHaveAHistoryOfHighCholesterolOrTriglycerides', 'Do you have a history of high cholesterol or triglycerides')}
                options={ms}
            />

            <RadioField
                name="hasStents"
                label={t('DashLabel_HaveYouEverHadAnyCoronaryStentsImplanted', 'Have you ever had any coronary stents implanted')}
                options={yn}
            />

            <RadioField
                name="historyChestPainExercise"
                label={t('DashLabel_DoYouSometimesGetChestPainWhenYouExerciseOrAreUnderStress', 'Do you sometimes get chest pain when you exercise or are under stress')}
                options={yn}
            />

            <RadioField
                name="historyChestPainOther"
                label={t('DashLabel_DoYouSometimesGetChestPainAtOtherTimes', 'Do you sometimes get chest pain at other times')}
                options={yn}
            />

            <SelectField
                name="resultsOfClimbingStairs"
                label={t('DashLabel_WhatHappensIfYouWalkUp2FlightsOfStairs', 'What happens if you walk up 2 flights of stairs')}
                options={model.climbingStairsOptions}
                placeholder={please}
            />

            {!model.useReducedQuestions && (
                <>
                    <RadioField
                        name="hadStressTestWithinYear"
                        label={t('DashLabel_HaveYouHadAStressTestWithinTheLastYear', 'Have you had a stress test within the last year')}
                        options={yn}
                    />
                    {watch('hadStressTestWithinYear') === yes && (
                        <div className="sub-question">
                            <TextField
                                name="stressTestLocation"
                                label={t('DashLabel_WhereWasTheStressTest', 'Where was the stress test')}
                                maxLength={128}
                            />
                            <RadioField
                                name="stressTestResults"
                                label={t('DashLabel_WhatDidTheStressTestShow', 'What did the stress test show')}
                                options={model.stressTestResultsOptions}
                            />
                        </div>
                    )}
                </>
            )}

            <RadioField
                name="hasHadStroke"
                label={t('DashLabel_HaveYouEverHadAStroke', 'Have you ever had a stroke')}
                options={yn}
            />
            {watch('hasHadStroke') === yes && (
                <div className="sub-question">
                    <NumberSelectField
                        name="howManyStrokes"
                        label={t('DashLabel_HowManyStrokesHaveYouHad', 'How many strokes have you had')}
                        max={5}
                        plusLast
                        placeholder={please}
                    />
                </div>
            )}

            <RadioField
                name="hasHadLegBypass"
                label={t('DashLabel_HaveYouEverHadBypassOperationsOnYourLegsToImproveB', 'Have you ever had bypass operations on your legs to improve blood flow')}
                options={yn}
            />
            {watch('hasHadLegBypass') === yes && (
                <div className="sub-question">
                    <NumberSelectField
                        name="howManyLegBypasses"
                        label={t('DashLabel_HowManyLegBypasses', 'How many leg bypasses')}
                        max={5}
                        plusLast
                        placeholder={please}
                    />
                </div>
            )}
        </>
    );
};

export default CardiovascularSection;
